/**
 * Hari penting kalender Islam (Hijriah) berbasis bulan-tanggal Hijri.
 * Key format: `${hMonth}-${hDay}`.
 * Sumber tanggal: literatur Islam standar; tanggal libur resmi
 * Indonesia (Idul Fitri, Idul Adha, dll) ditetapkan via sidang isbat
 * Kemenag dan dapat berbeda 1 hari dari hitungan aritmetika.
 */

export type HijriEventType = "hari-besar" | "puasa" | "penting";

export interface HijriEvent {
  hMonth: number; // 1-12
  hDay: number;   // 1-30
  name: string;
  nameEn: string;
  type: HijriEventType;
  descId: string;
  descEn: string;
}

export const HIJRI_EVENTS: HijriEvent[] = [
  { hMonth: 1, hDay: 1,
    name: "Tahun Baru Hijriah", nameEn: "Islamic New Year",
    type: "hari-besar",
    descId: "1 Muharram, awal tahun baru kalender Hijriah. Libur nasional di Indonesia.",
    descEn: "1 Muharram, the start of the Islamic calendar year. Public holiday in Indonesia." },
  { hMonth: 1, hDay: 10,
    name: "Hari Asyura", nameEn: "Day of Ashura",
    type: "puasa",
    descId: "10 Muharram, dianjurkan puasa sunnah Asyura.",
    descEn: "10 Muharram, recommended day for the Ashura fast." },
  { hMonth: 3, hDay: 12,
    name: "Maulid Nabi Muhammad SAW", nameEn: "Mawlid an-Nabi",
    type: "hari-besar",
    descId: "12 Rabiul Awal, peringatan kelahiran Nabi Muhammad SAW. Libur nasional.",
    descEn: "12 Rabi al-Awwal, commemoration of the birth of Prophet Muhammad. Public holiday." },
  { hMonth: 7, hDay: 27,
    name: "Isra Mikraj Nabi Muhammad SAW", nameEn: "Isra and Miraj",
    type: "hari-besar",
    descId: "27 Rajab, peringatan perjalanan malam Nabi Muhammad SAW. Libur nasional.",
    descEn: "27 Rajab, commemoration of the night journey of the Prophet. Public holiday." },
  { hMonth: 8, hDay: 15,
    name: "Nisfu Syaban", nameEn: "Mid-Shaban",
    type: "penting",
    descId: "15 Syaban, malam pengampunan dan dianjurkan ibadah.",
    descEn: "15 Shaban, night of forgiveness and recommended worship." },
  { hMonth: 9, hDay: 1,
    name: "Awal Ramadhan", nameEn: "Start of Ramadan",
    type: "puasa",
    descId: "1 Ramadhan, awal puasa wajib bulan Ramadhan.",
    descEn: "1 Ramadan, the start of the obligatory month-long fast." },
  { hMonth: 9, hDay: 17,
    name: "Nuzulul Quran", nameEn: "Nuzul al-Quran",
    type: "penting",
    descId: "17 Ramadhan, peringatan turunnya Al-Quran.",
    descEn: "17 Ramadan, commemoration of the revelation of the Quran." },
  { hMonth: 9, hDay: 21,
    name: "Awal 10 Malam Terakhir Ramadhan", nameEn: "Last Ten Nights of Ramadan",
    type: "penting",
    descId: "Mulai mencari malam Lailatul Qadar.",
    descEn: "Beginning of the search for Laylat al-Qadr." },
  { hMonth: 10, hDay: 1,
    name: "Idul Fitri", nameEn: "Eid al-Fitr",
    type: "hari-besar",
    descId: "1 Syawal, hari raya setelah Ramadhan. Libur nasional 2 hari.",
    descEn: "1 Shawwal, festival ending Ramadan. Two-day public holiday." },
  { hMonth: 12, hDay: 8,
    name: "Hari Tarwiyah", nameEn: "Day of Tarwiyah",
    type: "puasa",
    descId: "8 Zulhijah, hari persiapan jamaah haji.",
    descEn: "8 Dhul-Hijjah, day of preparation for hajj pilgrims." },
  { hMonth: 12, hDay: 9,
    name: "Hari Arafah", nameEn: "Day of Arafah",
    type: "puasa",
    descId: "9 Zulhijah, puasa sunnah Arafah, hari wukuf jamaah haji.",
    descEn: "9 Dhul-Hijjah, recommended Arafah fast and the day of standing at Arafat." },
  { hMonth: 12, hDay: 10,
    name: "Idul Adha", nameEn: "Eid al-Adha",
    type: "hari-besar",
    descId: "10 Zulhijah, hari raya kurban. Libur nasional.",
    descEn: "10 Dhul-Hijjah, festival of sacrifice. Public holiday." },
  { hMonth: 12, hDay: 11,
    name: "Hari Tasyrik", nameEn: "Days of Tashriq",
    type: "penting",
    descId: "11 Zulhijah, dilarang puasa, dianjurkan menyembelih kurban.",
    descEn: "11 Dhul-Hijjah, fasting forbidden, sacrifice still permitted." },
  { hMonth: 12, hDay: 12,
    name: "Hari Tasyrik", nameEn: "Days of Tashriq",
    type: "penting",
    descId: "12 Zulhijah, hari tasyrik kedua.",
    descEn: "12 Dhul-Hijjah, second day of Tashriq." },
  { hMonth: 12, hDay: 13,
    name: "Hari Tasyrik", nameEn: "Days of Tashriq",
    type: "penting",
    descId: "13 Zulhijah, hari tasyrik ketiga, akhir musim haji.",
    descEn: "13 Dhul-Hijjah, third day of Tashriq, end of hajj season." },
];

const EVENT_INDEX = new Map<string, HijriEvent>();
for (const e of HIJRI_EVENTS) {
  EVENT_INDEX.set(`${e.hMonth}-${e.hDay}`, e);
}

export function getHijriEvent(hMonth: number, hDay: number): HijriEvent | null {
  return EVENT_INDEX.get(`${hMonth}-${hDay}`) ?? null;
}

export function getHijriEventsForMonth(hMonth: number): HijriEvent[] {
  return HIJRI_EVENTS.filter((e) => e.hMonth === hMonth);
}